import CloseIcon from "@mui/icons-material/Close";
import { Box, Dialog, DialogActions, DialogContent, Divider, IconButton, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import CustomButton from "../Common/CustomButton";
import { TagsInputUniqueKeys } from "../Common/TagInputsUniqueKeys";
import useFetch from "../Common/useFetch";
import { AgreementStatus, AllowedFilterKeysVendor } from "./VendorDashboard";

export default function VendorDashboardFilter({
  open,
  onClose,
  setAppliedFilters,
  appliedFilters,
}: {
  open: boolean;
  onClose: () => void;
  setAppliedFilters: React.Dispatch<React.SetStateAction<Record<AllowedFilterKeysVendor, string[]>>>;
  appliedFilters: Record<AllowedFilterKeysVendor, string[]>;
}) {
  const [vendorNames, setVendorNames] = useState<string[]>(appliedFilters.vendorName || []);
  const [PANNumbers, setPANNumbers] = useState<string[]>(appliedFilters.PANNumber || []);
  const [statusOfAgreement, setStatusOfAgreement] = useState<string[]>(appliedFilters.statusOfAgreement || []);

  const [vendorNameOptions, setVendorNamesOptions] = useState<{ vendorName: string }[]>([]);
  const [PANOptions, setPANOptions] = useState<{ panNumber: string }[]>([]);

  const getFilterOptions = async () => {
    await useFetch("/vendor-master/filter/options", "GET", {
      failureMessage: "Failed to fetch filter options",
      thenCallBack: (response) => {
        const data = response.data.result;
        setVendorNamesOptions(
          data.vendorData.map((vendor) => {
            return { vendorName: vendor.vendorName };
          }) || []
        );
        setPANOptions(data.pan.map((pan: string) => ({ panNumber: pan })) || []);
      },
      errorCallback: (error) => {
        console.error("Error fetching filter options:", error);
      },
    });
  };

  useEffect(() => {
    getFilterOptions();
  }, []);

  const handleApplyFilter = () => {
    setAppliedFilters({
      vendorName: vendorNames.filter((v) => v.trim() !== ""),
      PANNumber: PANNumbers.filter((v) => v.trim() !== ""),
      statusOfAgreement: statusOfAgreement.filter((v) => v.trim() !== ""),
    });
    onClose();
  };

  const handleClearFilters = () => {
    onClose();
  };

  return (
    <Dialog open={open} sx={{ "& .MuiDialog-paper": { borderRadius: "4px", minWidth: "600px" } }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "16px 24px" }}>
        <Typography variant="h6" sx={{ flexGrow: 1 }}>
          Apply Filter
        </Typography>
        <IconButton aria-label="close" onClick={onClose} sx={{ position: "absolute", right: 8, top: 8 }}>
          <CloseIcon />
        </IconButton>
      </Box>
      <Divider />
      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column" }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "8px 16px",
            }}
          >
            <Typography variant="subtitle2">Vendor Name</Typography>
            <Box sx={{ width: "400px" }}>
              <TagsInputUniqueKeys
                tags={vendorNames}
                setTags={setVendorNames}
                fullWidth={true}
                placeholder="Newline separated Vendor Names"
                splitter="newline"
                limitTags={1}
                textFieldProps={{ multiline: true, maxRows: 1 }}
                options={vendorNameOptions}
                accessor={"vendorName"}
                uniqueKeyAccessor={"vendorName"}
              />
            </Box>
          </Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "8px 16px",
            }}
          >
            <Typography variant="subtitle2">PAN</Typography>
            <Box sx={{ width: "400px" }}>
              <TagsInputUniqueKeys
                tags={PANNumbers}
                setTags={setPANNumbers}
                fullWidth={true}
                placeholder="Newline separated PAN Numbers"
                splitter="newline"
                limitTags={1}
                textFieldProps={{ multiline: true, maxRows: 1 }}
                options={PANOptions}
                accessor={"panNumber"}
                uniqueKeyAccessor={"panNumber"}
              />
            </Box>
          </Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "8px 16px",
            }}
          >
            <Typography variant="subtitle2">Status Of Agreement</Typography>
            <Box sx={{ width: "400px" }}>
              <TagsInputUniqueKeys
                tags={statusOfAgreement}
                setTags={setStatusOfAgreement}
                fullWidth={true}
                placeholder="Newline separated Status Of Agreement"
                splitter="newline"
                limitTags={1}
                options={Object.values(AgreementStatus).map((status) => ({ status }))}
                textFieldProps={{ multiline: true, maxRows: 1 }}
                accessor={"status"}
                uniqueKeyAccessor={"status"}
                renderOption={(props, option) => (
                  <li {...props} key={option.status}>
                    {option.status.charAt(0).toUpperCase() + option.status.slice(1)}
                  </li>
                )}
              />
            </Box>
          </Box>
        </Box>
      </DialogContent>
      <Divider />
      <DialogActions>
        <CustomButton
          onClick={() => {
            handleClearFilters();
          }}
          variant="text"
        >
          Clear Filter
        </CustomButton>
        <CustomButton
          variant="contained"
          onClick={() => {
            handleApplyFilter();
          }}
        >
          Apply
        </CustomButton>
      </DialogActions>
    </Dialog>
  );
}
